import React from "react";
import { Download } from "lucide-react";
import { ReactComponent as NoInvoicesMark } from "../../../assets/svg/billing/no billing history icon.svg";
import {
  EmptyState,
  LoadPhase,
  SkeletonCard,
  useIsMobile,
} from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";
import { planLine } from "../invoiceLine";
import { formatMoney } from "../pricing";
import InvoiceCards from "./InvoiceCards";
import InvoiceStatusBadge from "./InvoiceStatusBadge";
import type { SubscriptionInvoice } from "../types/subscriptions.types";

/**
 * "Purchased Date" in the same words the plan card above uses: the day the
 * charge was taken is one thing on this screen, and one heading for it.
 *
 * Plan sits second, between the document and the money: once plans are bought
 * one at a time, "what was this charge for" is asked before "how much", and a
 * term named after the amount is a term read second.
 */
const COLUMNS = ["Invoice", "Plan", "Amount", "Purchased Date"];

const headCell =
  "px-3 sm:px-4 py-3 text-[13px] 3xl:text-[14px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] 3xl:text-[15px] text-ink";

/** What the table's own area shimmers as while the history is on its way: one
 *  block at the height a short history stands at, rather than a page of blocks
 *  in the shape of its cells. */
const HISTORY_SKELETON = "h-[220px]";

interface BillingHistoryCardProps {
  invoices: SubscriptionInvoice[];
  /**
   * Where the list is. Nothing is drawn while it is merely on its way; only a
   * wait long enough to notice shimmers, as one block the size of the table.
   */
  phase?: LoadPhase;
  /** Said in place of the rows when the list could not be loaded. */
  error?: string;
  /** Fetches this invoice's PDF and hands it to the browser. */
  onDownload: (invoice: SubscriptionInvoice) => void;
  /** The invoice whose PDF is being fetched, so one row spins and the rest
   *  stay pressable. */
  downloading?: string | null;
  /**
   * Whether anything has ever been bought, which is what an empty table means
   * here.
   *
   * A subscriber with no rows is a plan bought minutes ago whose first charge
   * has not settled — a gap of hours, said in one line. A workspace on the free
   * trial has bought nothing at all and may never have: that is a list that has
   * never had a row in it rather than a list waiting on one, and it gets the
   * first-run state with the mark on it.
   */
  purchased?: boolean;
}

/**
 * Everything the workspace has been charged, newest first.
 *
 * A record rather than a workspace: nothing here is clickable except the
 * download, because an invoice is a document and not a screen — what it says
 * is on the PDF the backend renders, which is the copy that has the invoice
 * number and the registered details on it.
 *
 * The count is in the heading because it is the answer to the only question
 * asked of a history at a glance — how many times have I been charged.
 *
 * On a phone the rows are cards and there is no card around them — see
 * `InvoiceCards`. The chrome goes with the table: a heading, a border and a
 * white fill around a stack of white cards is a box inside a box, and the
 * phone's own screens put a caption over a group rather than a frame round it.
 * Everything else on the section — what it is called, the count beside it, the
 * three states before a row exists — is one piece of markup either way.
 */
const BillingHistoryCard: React.FC<BillingHistoryCardProps> = ({
  invoices,
  phase = "ready",
  error,
  onDownload,
  downloading = null,
  purchased = true,
}) => {
  // The one size question this card asks. Answered here rather than in CSS
  // because the two are different markup and not two skins of one: drawing
  // both would put every invoice on the screen twice, and a download button
  // that is found twice is a download button a test cannot press.
  const isMobile = useIsMobile();

  return (
    <section
      aria-labelledby="billing-history-heading"
      className="md:rounded-md md:border md:border-line md:bg-white"
    >
      <h2
        id="billing-history-heading"
        className="pb-3 text-[15px] font-medium text-ink md:px-5 md:py-4 md:text-[16px] 3xl:px-6 3xl:py-5 3xl:text-[18px]"
      >
        Billing history{" "}
        {/* Zero-padded to two digits, the way the design writes it — and only
            once the list is actually known, so a count is never quoted off an
            answer that has not arrived. */}
        {phase === "ready" && (
          <span className="text-ink-muted">
            ({String(invoices.length).padStart(2, "0")})
          </span>
        )}
      </h2>

      {error ? (
        <p className="text-[14px] text-ink-muted md:border-t md:border-line md:px-5 md:py-6 3xl:px-6 3xl:text-[15px]">
          {error}
        </p>
      ) : phase !== "ready" ? (
        // The list's own area, standing in for the whole history.
        <div className="md:px-5 md:pb-5 3xl:px-6 3xl:pb-6">
          <SkeletonCard phase={phase} className={HISTORY_SKELETON} />
        </div>
      ) : isMobile ? (
        <InvoiceCards
          invoices={invoices}
          onDownload={onDownload}
          downloading={downloading}
          purchased={purchased}
        />
      ) : (
        // The card's own scroll region: three columns and a download do not fit
        // a phone, and the page behind must not scroll sideways for them.
        <div className="overflow-x-auto custom-scrollbar-overlay">
          <table
            className={`w-full table-fixed border-collapse text-left ${
              // The width the four columns need is only worth insisting on when
              // there are charges to lay out in them. With none, it would push
              // the state below past a phone’s edge and leave the reader
              // scrolling sideways to find out why the list is empty.
              invoices.length === 0 ? "" : "min-w-[620px]"
            }`}
          >
            <colgroup>
              {/* The number and its badge share a column, so it is the widest
                  of them. */}
              <col className="w-[30%]" />
              <col className="w-[26%]" />
              <col className="w-[16%]" />
              <col className="w-[20%]" />
              <col className="w-[8%]" />
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((heading) => (
                  <th key={heading} scope="col" className={headCell}>
                    {heading}
                  </th>
                ))}
                {/* The download column is headed by what the buttons in it do,
                    said only to a screen reader — a word above a row of icons
                    would be a label for something already labelled. */}
                <th scope="col" className={headCell}>
                  <span className="sr-only">Invoice PDF</span>
                </th>
              </tr>
            </thead>

            {invoices.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="px-5 3xl:px-6">
                    {purchased ? (
                      // Not an empty state with a picture: a plan bought minutes
                      // ago has no invoice until the first charge settles, and
                      // this is that gap said in one line.
                      <p className="py-8 text-center text-[14px] 3xl:text-[15px] text-ink-muted">
                        Your invoices will appear here once your first payment has
                        been collected.
                      </p>
                    ) : (
                      // A trial has never been charged for anything, so this is
                      // the first-run state and not a list reporting a result —
                      // see `purchased`. Kept under the column heads rather than
                      // replacing the table: the headings are what say what a
                      // charge will be recorded as when one is taken.
                      <EmptyState
                        variant="welcome"
                        // The tab's own type scale. This sits under a plan card
                        // and a real heading, and at page size the headline here
                        // would outshout both.
                        size="sm"
                        illustration={
                          <NoInvoicesMark aria-hidden className="h-14 w-14" />
                        }
                        title="No billing history yet"
                        // Broken after the first sentence as the design has it:
                        // one says nothing is wrong, the other says where these
                        // will turn up. It still wraps normally on a narrow
                        // screen.
                        description={
                          <>
                            You haven&apos;t purchased a paid subscription yet.
                            Once you do,
                            <br />
                            your invoices and payment history will appear here.
                          </>
                        }
                      />
                    )}
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors"
                  >
                    <td className={bodyCell}>
                      {/* The badge keeps its size and the number gives way:
                          where the term stands is the shorter fact and the one
                          that stops being readable if it is clipped. */}
                      <div className="flex items-center gap-2">
                        <span className="truncate">{invoice.number}</span>
                        <InvoiceStatusBadge status={invoice.status} />
                      </div>
                    </td>
                    <td className={`${bodyCell} truncate text-ink-muted`}>
                      {planLine(invoice)}
                    </td>
                    <td className={`${bodyCell} tabular-nums`}>
                      {formatMoney(invoice.amount)}
                    </td>
                    <td className={bodyCell}>
                      {formatCreatedOn(new Date(invoice.issuedOn))}
                    </td>
                    <td className={bodyCell}>
                      <button
                        type="button"
                        onClick={() => onDownload(invoice)}
                        disabled={downloading === invoice.id}
                        // Every row's button is the same icon, so the name says
                        // which invoice this one fetches.
                        aria-label={`Download invoice ${invoice.number}`}
                        className="ml-auto flex h-8 w-8 items-center justify-center rounded-md text-brand transition-colors hover:bg-brand-soft disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {downloading === invoice.id ? (
                          <span
                            aria-hidden="true"
                            className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                          />
                        ) : (
                          <Download aria-hidden="true" className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      )}
    </section>
  );
};

export default BillingHistoryCard;
